/**
 * @component ry-nag
 * @category  feedback
 * @summary   Sticky dismissible banner (Fomantic UI `.ui.nag` equivalent). A full-width strip that pins to the top or bottom of the viewport (or a relative container) until the user dismisses it. Optional cookie/localStorage persistence so it stays dismissed across reloads. Use for cookie consent, system notices, browser-version warnings.
 *
 * Reference: Fomantic UI's nag module. ry-nag is the smaller, sticky-banner
 * cousin of `<ry-toast>` (transient overlay) and `<ry-alert>` (in-flow). Use
 * the right tool: ry-nag for site-wide persistent notices, ry-toast for
 * transient confirmations, ry-alert for in-page validation errors.
 *
 * @attribute position  'top' | 'bottom' ?   default="top"   Pin edge.
 * @attribute persist   string?              localStorage key. When set, ry-nag remembers the dismissal across reloads — it auto-hides at mount if the key is present. Without this attribute, every reload re-shows the nag.
 * @attribute closable  boolean   default=true   Renders the close button. Set `closable="false"` to lock the nag visible until programmatically dismissed (e.g. cookie consent that requires explicit action).
 *
 * @property  dismissed   boolean   Read-only — true after `dismiss()` has fired. Use to check state synchronously.
 *
 * @method    dismiss()   Hide the nag + persist (if `[persist]`) + fire `ry:dismiss`. No-op if already dismissed.
 * @method    show()      Force-show a previously-dismissed nag + clear persisted state (if `[persist]`). Fires `ry:show`.
 *
 * @event ry:beforedismiss
 *        detail: {}
 *        fires:  at the top of `dismiss()` — close-button click or `el.dismiss()` — after the already-dismissed guard, before the state change AND before the `[persist]` key is written
 *        cancelable: preventDefault() keeps the nag visible and nothing is persisted; `ry:dismiss` does not fire
 *
 * @event ry:dismiss
 *        detail: {}
 *        fires:  on user click of the close button or programmatic `dismiss()`.
 *
 * @event ry:beforeshow
 *        detail: {}
 *        fires:  at the top of `show()` — after the already-visible guard, before the state change and before the `[persist]` key is cleared
 *        cancelable: preventDefault() keeps the nag dismissed and the persisted flag intact; `ry:show` does not fire
 *
 * @event ry:show
 *        detail: {}
 *        fires:  when `show()` re-shows a previously-dismissed nag (clears the persisted flag).
 *
 * @state     `data-ry-state` of `"dismissed"` when hidden; otherwise unset. CSS reads the state to apply `display: none`.
 *
 * @content   Free-form. Typically a short message + optional inline action button. The close button is auto-injected at the end of the content (when `[closable]`).
 *
 * @dom
 *   ry-nag[position?][persist?][closable?][data-ry-state?]   role="region"   aria-label="Notice"?
 *     > {authored content}
 *     > button.ry-nag__close   [data-ry-target="close"]   aria-label="Dismiss"   (only when [closable])
 *
 * @gotcha  ry-nag uses `position: fixed` (top OR bottom) — it pins to the VIEWPORT, not its parent. If you place it inside a scrolling container, it ignores the container and overlays the page. To pin to a container, override CSS with `position: sticky` and adjust the container.
 *
 * @gotcha  `[persist]` uses `localStorage` (key: `"ry-nag:<persist>"`). Pages with strict storage policies (Safari ITP, private browsing) may silently fail to persist — the nag re-shows on reload. Wrap your usage in a try/catch if you depend on it.
 *
 * @gotcha  Setting `[closable="false"]` AND not exposing a `dismiss()` call elsewhere creates an undismissable nag (cookie consent that traps the user). Only use this when paired with another action button in your content that calls `el.dismiss()`.
 *
 * @gotcha  `dismiss()` and `show()` are silent for state that already matches — `dismiss()` on an already-dismissed nag is a no-op, `show()` on a visible nag is a no-op. Neither fires the corresponding event in the no-op case.
 *
 * @gotcha  Auto-dismiss at mount (when `[persist]` is set and the key exists in storage) does NOT fire `ry:dismiss` — only user-initiated or `dismiss()` calls fire the event. This is intentional so consumers don't see a phantom dismiss on every reload.
 *
 * @example basic
 * ```html
 * <ry-nag>
 *   We use cookies to improve your experience.
 *   <a href="/privacy">Learn more</a>
 * </ry-nag>
 * ```
 *
 * @example bottom-with-persistence
 * ```html
 * <ry-nag position="bottom" persist="cookie-consent">
 *   By using this site you agree to our terms.
 * </ry-nag>
 * ```
 *
 * @example non-closable-cookie-consent
 * ```html
 * <ry-nag persist="cookies-v2" closable="false" id="consent">
 *   Cookies?
 *   <ry-button onclick="document.getElementById('consent').dismiss()">Accept</ry-button>
 * </ry-nag>
 * ```
 *
 * @tutorial mount-renders-close-button-by-default
 * ```js
 * const el = mount('<ry-nag>Hello</ry-nag>');
 * await expectEqual(!!el.querySelector('[data-ry-target="close"]'), true);
 * ```
 *
 * @tutorial closable-false-omits-close-button
 * ```js
 * const el = mount('<ry-nag closable="false">Locked</ry-nag>');
 * await expectEqual(el.querySelector('[data-ry-target="close"]'), null);
 * ```
 *
 * @tutorial dismiss-method-hides-and-fires
 * ```js
 * const el = mount('<ry-nag>x</ry-nag>');
 * const fired = [];
 * el.addEventListener('ry:dismiss', () => fired.push(1));
 * el.dismiss();
 * await expectEqual(el.dismissed, true);
 * await expectAttr(el, 'data-ry-state', 'dismissed');
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial dismiss-is-idempotent
 * ```js
 * // Wrong assumption: dismiss() always fires ry:dismiss. Calling it on an
 * // already-dismissed nag is a no-op.
 * const el = mount('<ry-nag>x</ry-nag>');
 * el.dismiss();
 * const fired = [];
 * el.addEventListener('ry:dismiss', () => fired.push(1));
 * el.dismiss();
 * el.dismiss();
 * await expectEqual(fired.length, 0);
 * ```
 *
 * @tutorial close-button-click-dismisses
 * ```js
 * const el = mount('<ry-nag>x</ry-nag>');
 * const fired = [];
 * el.addEventListener('ry:dismiss', () => fired.push(1));
 * el.querySelector('[data-ry-target="close"]').click();
 * await expectEqual(el.dismissed, true);
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial show-method-clears-dismissed-state
 * ```js
 * const el = mount('<ry-nag>x</ry-nag>');
 * el.dismiss();
 * const fired = [];
 * el.addEventListener('ry:show', () => fired.push(1));
 * el.show();
 * await expectEqual(el.dismissed, false);
 * await expectEqual(el.dataset.ryState, undefined);
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial persist-saves-to-localstorage
 * ```js
 * try { localStorage.removeItem('ry-nag:t1'); } catch (e) {}
 * const el = mount('<ry-nag persist="t1">x</ry-nag>');
 * el.dismiss();
 * await expectEqual(localStorage.getItem('ry-nag:t1'), '1', 'persisted under namespaced key');
 * try { localStorage.removeItem('ry-nag:t1'); } catch (e) {}
 * ```
 *
 * @tutorial persist-restores-dismissed-on-mount-silently
 * ```js
 * // Wrong assumption: ry:dismiss fires when the persisted flag triggers
 * // auto-hide at mount. It does NOT — only user-initiated/programmatic
 * // dismisses fire the event.
 * localStorage.setItem('ry-nag:t2', '1');
 * const el = mount('<ry-nag persist="t2">x</ry-nag>');
 * await expectEqual(el.dismissed, true, 'auto-dismissed at mount');
 * try { localStorage.removeItem('ry-nag:t2'); } catch (e) {}
 * ```
 *
 * @tutorial beforedismiss-veto
 * ```js
 * // preventDefault() on ry:beforedismiss keeps the nag visible — and the
 * // veto happens BEFORE the [persist] key is written, so nothing is stored.
 * try { localStorage.removeItem('ry-nag:t4'); } catch (e) {}
 * const el = mount('<ry-nag persist="t4">x</ry-nag>');
 * const dismissed = [];
 * el.addEventListener('ry:dismiss', () => dismissed.push(1));
 * el.addEventListener('ry:beforedismiss', (e) => e.preventDefault(), { once: true });
 * el.querySelector('[data-ry-target="close"]').click();
 * await expectEqual(el.dismissed, false, 'vetoed dismiss leaves it visible');
 * await expectEqual(localStorage.getItem('ry-nag:t4'), null, 'nothing persisted on veto');
 * await expectEqual(dismissed.length, 0, 'ry:dismiss did not fire');
 * el.dismiss();
 * await expectEqual(el.dismissed, true, 'without the veto, dismiss() works');
 * await expectEqual(dismissed.length, 1, 'ry:dismiss fired once');
 * try { localStorage.removeItem('ry-nag:t4'); } catch (e) {}
 * ```
 *
 * @tutorial beforeshow-veto
 * ```js
 * // preventDefault() on ry:beforeshow keeps a dismissed nag dismissed (and
 * // the persisted flag intact).
 * localStorage.setItem('ry-nag:t5', '1');
 * const el = mount('<ry-nag persist="t5">x</ry-nag>');
 * const shown = [];
 * el.addEventListener('ry:show', () => shown.push(1));
 * el.addEventListener('ry:beforeshow', (e) => e.preventDefault(), { once: true });
 * el.show();
 * await expectEqual(el.dismissed, true, 'vetoed show leaves it dismissed');
 * await expectEqual(localStorage.getItem('ry-nag:t5'), '1', 'persisted flag untouched on veto');
 * await expectEqual(shown.length, 0, 'ry:show did not fire');
 * el.show();
 * await expectEqual(el.dismissed, false, 'without the veto, show() works');
 * await expectEqual(shown.length, 1, 'ry:show fired once');
 * try { localStorage.removeItem('ry-nag:t5'); } catch (e) {}
 * ```
 *
 * @tutorial show-clears-persisted-value
 * ```js
 * localStorage.setItem('ry-nag:t3', '1');
 * const el = mount('<ry-nag persist="t3">x</ry-nag>');
 * el.show();
 * await expectEqual(localStorage.getItem('ry-nag:t3'), null, 'show() removes persisted key');
 * ```
 *
 * @see ry-toast
 * @see ry-alert
 */

import { RyElement } from '../core/ry-element.js';

export class RyNag extends RyElement {
  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
    if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Notice');

    const closable = this.getAttribute('closable') !== 'false';
    if (closable && !this.querySelector('[data-ry-target="close"]')) {
      const btn = document.createElement('button');
      btn.setAttribute('data-ry-target', 'close');
      btn.setAttribute('aria-label', 'Dismiss');
      btn.setAttribute('type', 'button');
      btn.className = 'ry-nag__close';
      btn.innerHTML = '&times;';
      this.appendChild(btn);
      this.on(btn, 'click', () => this.dismiss());
    }

    // Auto-dismiss silently if previously persisted
    if (this.#isPersisted()) {
      this.dataset.ryState = 'dismissed';
    }
  }

  get dismissed(): boolean {
    return this.dataset.ryState === 'dismissed';
  }

  dismiss(): void {
    if (this.dismissed) return;
    if (!this.emit('beforedismiss')) return;
    this.dataset.ryState = 'dismissed';
    this.#savePersist();
    this.emit('dismiss');
  }

  show(): void {
    if (!this.dismissed) return;
    if (!this.emit('beforeshow')) return;
    delete this.dataset.ryState;
    this.#clearPersist();
    this.emit('show');
  }

  #persistKey(): string | null {
    const key = this.getAttribute('persist');
    return key ? `ry-nag:${key}` : null;
  }

  #isPersisted(): boolean {
    const key = this.#persistKey();
    if (!key) return false;
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #savePersist(): void {
    const key = this.#persistKey();
    if (!key) return;
    try {
      localStorage.setItem(key, '1');
    } catch { /* storage unavailable */ }
  }

  #clearPersist(): void {
    const key = this.#persistKey();
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch { /* storage unavailable */ }
  }
}

customElements.define('ry-nag', RyNag);
